import HelpIcon from '@mui/icons-material/Help'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import IconButton from '@mui/material/IconButton'
import Rating from '@mui/material/Rating'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import { useQuery } from '@tanstack/react-query'
import { type Driver, driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { sleep } from 'global/helpers/sleep'
import i18n from 'i18n'
import { type ReactElement, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { getUserPreferences, saveUserPreferences } from 'screens/UserManager/UserManager.service'
import { WelcomeGuideStatus } from 'screens/UserManager/UserManager.types'
import { weblinkTokenQueryOptions } from 'screens/Weblink/Weblink.service'
import StarRatingDialog from './StarRatingDialog'
import { getGuideName, getGuideSteps } from './Support.helpers'
import SupportRequestDialog from './SupportRequestDialog'

const CustomIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: '#fff',
  borderTopLeftRadius: 4,
  borderBottomLeftRadius: 4,
  borderBottomRightRadius: 0,
  borderTopRightRadius: 0,
  minWidth: 'auto',
  boxShadow: theme.shadows[4],
  '&:hover': {
    backgroundColor: '#fff',
  },
}))

export const getGuideDriver = async (guideName: string, manual = false): Promise<Driver> => {
  const guide = driver({
    showProgress: true,
    showButtons: ['next', 'close'],
    nextBtnText: i18n.t('general.next'),
    doneBtnText: i18n.t('general.close'),
    progressText: '{{current}} / {{total}}',
    steps: getGuideSteps(guideName),
    onDestroyed: () => {
      if (manual) {
        return
      }
      (async () => {
        await saveUserPreferences({ [guideName]: WelcomeGuideStatus.DONE })
      })()
    },
  })

  if (!manual) {
    try {
      await sleep(2000)
      const preferences = await getUserPreferences()
      if (preferences[guideName] === WelcomeGuideStatus.PENDING) {
        guide.drive()
      }
    } catch {
      //
    }
  }

  return guide
}

const Support = (): ReactElement => {
  const location = useLocation()
  const { jwt } = useParams<{ jwt: string }>()
  const [activeGuide, setActiveGuide] = useState<Driver>()
  const [supportRequestDialogOpen, setSupportRequestDialogOpen] = useState(false)
  const [hover, setHover] = useState(false)
  const [starValue, setStarValue] = useState<number>(0)
  const [starRatingDialogOpen, setStarRatingDialogOpen] = useState(false)
  const tokenQuery = useQuery({
    ...weblinkTokenQueryOptions(jwt),
    enabled: !!jwt,
  })
  const weblinkToken = tokenQuery.data?.token

  const fetchGuideState = async (): Promise<void> => {
    const guideName = getGuideName(getRouteName(location.pathname))
    if (!guideName) {
      return
    }

    setActiveGuide(await getGuideDriver(guideName))
  }

  useEffect(() => {
    if (starValue !== 0) {
      setStarRatingDialogOpen(true)
    }
  }, [starValue])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchGuideState()
  }, [])

  return (
    <>
      <Stack
        spacing={1}
        color="primary"
        sx={{
          position: 'fixed',
          zIndex: 1200,
          bottom: '10rem',
          minWidth: '2.5rem',
          right: 0,
        }}
      >
        <CustomIconButton
          data-guide="satisfaction"
          color="inherit"
          onMouseEnter={(): void => setHover(true)}
          onMouseLeave={(): void => setHover(false)}
        >
          {!hover && <StarBorderIcon fontSize="medium" color="warning" />}
          {hover && <Rating
            name="starRating"
            value={starValue}
            emptyIcon={<StarBorderIcon fontSize="inherit" color="warning" />}
            onChange={(_event, newValue): void => {
              if (newValue != null) {
                setStarValue(newValue)
                analyticsEvent('polestar_cs_support_star_rating_click')
              }
            }}
          />}
        </CustomIconButton>
      </Stack>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          position: 'fixed',
          zIndex: 1200,
          bottom: '4rem',
          right: 0,
          width: '2.5rem',
        }}
      >
        {activeGuide && (
          <CustomIconButton
            color="inherit"
            onClick={(): void => {
              activeGuide.drive()
            }}
          >
            <HelpIcon fontSize="medium" color="primary" />
          </CustomIconButton>
        )}
        <CustomIconButton
          data-guide="feedback"
          color="inherit"
          onClick={(): void => {
            analyticsEvent('polestar_cs_support_request_click')
            setSupportRequestDialogOpen(true)
          }}
        >
          <QuestionAnswerIcon fontSize="medium" color="primary" />
        </CustomIconButton>
      </Stack>

      <SupportRequestDialog
        payload={{
          weblinkToken,
        }}
        open={supportRequestDialogOpen}
        onAction={(): void => {
          setSupportRequestDialogOpen(false)
        }}
        onClose={(): void => {
          setSupportRequestDialogOpen(false)
        }}
      />

      <StarRatingDialog
        payload={{
          weblinkToken,
          starRating: starValue,
        }}
        open={starRatingDialogOpen}
        onAction={(): void => {
          setStarRatingDialogOpen(false)
        }}
        onClose={(): void => {
          setStarRatingDialogOpen(false)
        }}
      />
    </>
  )
}

export default Support
